/**
 * Button — exemplar primitive demonstrating every convention from
 * frontend-conventions.md §10:
 *   - forwardRef forwarding to root element
 *   - className accepted and merged onto root
 *   - data-testid prop accepted and forwarded
 *   - Variants via props (not separate components)
 *   - All visual values via CSS variables
 *   - Affirmative boolean prop names (isLoading)
 *   - Domain-verb callback names (onClick — HTML standard)
 *   - Prop spreading for unknown HTMLButtonAttributes
 */

import { forwardRef, type ButtonHTMLAttributes } from 'react';
import styles from './Button.module.css';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
}

// Explicit maps — Vite's CSS Modules camelCaseOnly converts .variant-primary
// to styles.variantPrimary; dynamic kebab-case keys would silently miss.
// Record type uses `string | undefined` because noUncheckedIndexedAccess on
// CSS Module types yields undefined for missing classes; filter(Boolean)
// below drops them at runtime, and the variant/size tests assert presence.
const VARIANT_CLASS: Record<ButtonVariant, string | undefined> = {
  primary: styles.variantPrimary,
  secondary: styles.variantSecondary,
  ghost: styles.variantGhost,
  danger: styles.variantDanger,
};

const SIZE_CLASS: Record<ButtonSize, string | undefined> = {
  sm: styles.sizeSm,
  md: styles.sizeMd,
  lg: styles.sizeLg,
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', isLoading = false, className, children, disabled, ...rest },
  ref,
) {
  const composedClassName = [
    styles.btn,
    VARIANT_CLASS[variant],
    SIZE_CLASS[size],
    isLoading ? styles.loading : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      ref={ref}
      className={composedClassName}
      disabled={disabled ?? isLoading}
      aria-busy={isLoading || undefined}
      {...rest}
    >
      {children}
    </button>
  );
});
